import React, { Component, useEffect, useRef, useState } from 'react';
import DropzoneComponent from 'react-dropzone-component';
import ReactDOMServer from 'react-dom/server';
import { FormattedMessage, injectIntl } from 'react-intl';
import {parseClasses, getFileSize, isNotEmpty, i18nIntl, cleanFieldProps} from '../../lib/ep.js';

import { SecondaryButton, SquaredButton } from './ActionButton';
import ButtonCollection from './ButtonCollection';
import FieldLabel from './FieldLabel';
import FieldErrorMessage from './FieldErrorMessage';
import { DeleteButton } from './ConfirmButton.js';
import Spinner from './Spinner.js';
import FloatingLayer from './FloatingLayer.js';
import SectionTitle from './SectionTitle.js';
import SectionSubtitle from './SectionSubtitle.js';
import FieldWrapper from './FieldWrapper.js';
import { SimpleAudioControls } from './SoundSelect.js';
import Tooltip from './Tooltip.js';

var componentConfig = {
	iconFiletypes: ['.jpg', '.png', '.gif', '.webp'],
	showFiletypeIcon: true,
	postUrl: '/uploadHandler',
	showFiletypeIcon:false
};

var djsConfig = {
	autoProcessQueue: false,
	thumbnailWidth:null,
	thumbnailHeight:null,
	createImageThumbnails:false,
	previewTemplate: ReactDOMServer.renderToStaticMarkup(
		<div>
			<div className="dz-details">
				<img data-dz-thumbnail="true" />
			</div>
			<div className="dz-progress"><span className="dz-upload" data-dz-uploadprogress="true"></span></div>
		</div>
	)
}

var mimetypes = {
	all:[],
	image:['image/jpeg','image/gif','image/png','image/bmp','image/tiff','image/x-icon','image/webp','image/heic','image/heif'],
	video:['video/x-ms-asf', 'video/x-ms-wmv', 'video/x-ms-wmx', 'video/x-ms-wm', 'video/avi', 'video/divx', 'video/x-flv', 'video/quicktime', 'video/mpeg', 'video/mp4', 'video/ogg', 'video/webm', 'video/x-matroska'],
	audio:['audio/mpeg', 'audio/mpeg', 'audio/x-mpequrl', 'audio/mp3', 'audio/x-realaudio', 'audio/wav', 'audio/ogg', 'audio/midi', 'audio/x-ms-wma', 'audio/x-ms-wax', 'audio/x-matroska'],
	text:['text/plain', 'text/csv', 'text/richtext', 'text/html', 'application/rtf', 'application/msword', 'application/vnd.ms-powerpoint', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'application/vnd.oasis.opendocument.text', 'application/vnd.oasis.opendocument.presentation', 'application/vnd.oasis.opendocument.spreadsheet', 'application/pdf'],
	application:['application/pdf'/*,'application/x-tar','application/zip','application/x-gzip','application/rar'*/]
}
for(var i  in mimetypes) { for (var j  in mimetypes[i]) { mimetypes['all'].push(mimetypes[i][j]);}}

const successUpload = (k, props, response,name) => {
	if(response.status) {
		let full = `full_${k}`;
		if(props.changeMulti) {
			if (response.name == undefined) response.name = "";
			if (response.full == undefined) response.full = "";

			props.changeMulti({
				[k]:response.name,
				[full]:response.full
			});
		} else {
			let fname = response.name ?? "";
			if (response.filename != undefined) {
				fname = response.filename;
			}
			
			props.change(k,fname, name,response.full);
			props.change(`full_${k}`, response.full ?? '',name);
		}

		if(props.hooks != undefined) props.hooks({ type:'AUTO_IMAGE_END', response });
	}
};

export const AutoPostFileUploader = injectIntl((props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ uploading, setUploading ] = useState(false);
	const [ progress, setProgress ] = useState(0);
	const [ size_error, setSizeError ] = useState(false);

	const max_weight = (isNotEmpty(props, '_max_weight')) ? (1024 * props._max_weight) : false;

	const remove = () => {
		if(props.delete) props.delete();
		if(props.change != undefined) {
			props.change(props.name,null);
			props.change('full_' + props.name, null);
		}
		if(props.onRemove) props.onRemove();
	}

	const autodjsConfig = {
		...djsConfig,
		autoProcessQueue:true,
		url:props.auto_url,
		paramName:'image',
	};

	const eventHandlers = {
		sending:(file, xhr, formData) => {
			xhr.open('POST', `${props.auto_url}/uploadSize_${getFileSize(file.size,0).replace(' ','')}`, true);

			if(max_weight) {
				const max_size_permitted = max_weight * 1024 * 1024;
				if(max_size_permitted < file.size) {
					setSizeError(file.size);
					return;
				}
			}

			setUploading(true);
			setProgress(0);
			if(props.fileAdded != undefined) props.fileAdded(file, xhr, formData);
			if(props.hooks != undefined) props.hooks({ type:'AUTO_IMAGE_START' });
			if(props.image != undefined) {
				formData.append('name', props.image.split('/').slice(-1)[0]);
			}
			formData.append('type', props.auto_type);
			if(props?._size) {
				if(props._size.fit != undefined) formData.append('fit', props._size.fit);
				if(props._size.width != undefined) formData.append('width', props._size.width);
				if(props._size.height != undefined) formData.append('height', props._size.height);
			}
		},
		uploadprogress:(file, percent, bytes) => {
			setProgress(percent);
		},
		success:(file, response) => {
			response = JSON.parse(response);
			successUpload(props.name, props, response, file.name);
			setUploading(false);
		}
	}

	const is_upload_finished = (progress != undefined && progress > 99.5) ? true : false;
	
	const file = props.model[props.name];
	const full_file = (props.model[`full_${props.name}`] != undefined) ? props.model[`full_${props.name}`] : file;

	const has_file = (file != '' && file != undefined) ? true : false;
	
	const wrapper_props = cleanFieldProps(props);
	
	const file_type = props?.file_type ?? ['file'];

	let enable_play = false;
	let enable_preview = false;
	let enable_download = false;

	let accepted_formats = props?._accepted_formats ?? [];
	if(accepted_formats.length == 0) {
		if(file_type.includes('audio')) {
			accepted_formats = [...accepted_formats, 'MP3'];
		}

		if(file_type.includes('file') || file_type.includes('document')) {
			accepted_formats = [...accepted_formats, 'TXT', 'PDF', 'DOC', 'DOCX'];
		}
	}

	if(file_type.includes('audio')) {
		enable_play = true;
	}

	if(file_type.includes('image')) {
		enable_preview = true;
	}

	if(file_type.includes('file') || file_type.includes('document')) {
		enable_download = true;
	}

	if(accepted_formats.length > 0) {
		wrapper_props.after_component = `${i18n("Valid formats")}: ${accepted_formats.join(", ")}`;
	}

	if(max_weight !== false) {
		wrapper_props.after_component = (wrapper_props.after_component != undefined) ? `${wrapper_props.after_component}<br />` : "";
		wrapper_props.after_component += `${i18n("Max.")} ${Math.ceil(10 * max_weight / 1024) / 10} MB`;
	}

	if(isNotEmpty(wrapper_props, 'after_component')) {
		wrapper_props.after_component = <span dangerouslySetInnerHTML={{ __html:wrapper_props.after_component }} />;
	}

	return (
		<>
			<FieldWrapper type='file' {...wrapper_props}>
				{(props.custom_input_contents != undefined) && (
					<span class="extra_input">{props.custom_input_contents}</span>
				)}

				<figure class="is_auto image_uploader file_uploader with_dragger gui_pos_rel">
					<DropzoneComponent
						config={componentConfig}
						eventHandlers={eventHandlers}
						djsConfig={autodjsConfig}
					>
						{(has_file) && (
							<div class="uploader_preview img_preview_bg">
								<div class="dz-message has_file">
									<span>[ {i18n("File uploaded")} ]</span>
								</div>
							</div>
						)}
						
						{(!has_file) && (
							<div>
								<div class="dz-message">
									<span class="drag_message">
										<span class="original_color_item">{i18n("Upload file")}</span>
									</span>
								</div>

								<img src={`${window.cdn_url}img/blank.png`} width="100%" height="80" />
							</div>
						)}
					</DropzoneComponent>

					{(has_file) && (
						<ButtonCollection _class={{ other:'floating_actions' }}>
							{(enable_download) && (
								<SquaredButton
									_href={(enable_preview) ? full_file : props.preview_file}
									_target="_blank"
									icon='knk-download transparentButton'
								/>
							)}

							{(enable_preview) && (
								<SquaredButton
									icon='knk-visible transparentButton'
									tooltip={<Tooltip position='top' with_icon={false}>{`<img src="${full_file}" alt="" />`}</Tooltip>}
								/>
							)}

							{(enable_play) && (
								<SimpleAudioControls
									audio={props.file}
									extra_props={{
										loop:true,
										//html5:true,
									}}
									is_custom={true}
								/>
							)}

							<DeleteButton
								squared={true}
								onClick={() => remove()}
							/>
						</ButtonCollection>
					)}
				</figure>

				{(uploading) && (
					<div class="uploading_media is_fullscreen relative_to_container">
						<Spinner
							force_bg={true}
							hide_spinner={is_upload_finished}
							autotranslate={false}
						>{(!is_upload_finished) ? `${i18n("Finishing")}...` : null}</Spinner>

						<div class="uploading_progress_bar">
							<span
								class={`bar gui_is_animated${(is_upload_finished) ? ' is_done' : ''}`}
								style={{ width:`${progress}%` }}
							/>
						</div>
					</div>
				)}
			</FieldWrapper>

			{(size_error !== false) && (
				<FloatingLayer
					_class={{ size:'small' }}
					backButton={() => setSizeError(false)}
				>
					<div>
						<SectionTitle>The file is too big</SectionTitle>

						<SectionSubtitle autotranslate={false}>
							<FormattedMessage
								id="You have exceeded the maximum file size{max}"
								values={{
									max:(max_weight !== false) ? `: ${getFileSize(1024 * max_weight)}` : "",
								}}
							/>
						</SectionSubtitle>
		
						<ButtonCollection in_floating={true}>
							<SecondaryButton
								onClick={() => setSizeError(false)}
							>Close</SecondaryButton>
						</ButtonCollection>
					</div>
				</FloatingLayer>
			)}
		</>
	)
});

class FileUploader extends Component {
	constructor(props) {
		super();
		this.state = {
			error:null,
			file:(props.file != undefined) ? props.file : '',
			file_name:'',
			image:(props.image != undefined) ? props.image : '',
		}
		this.eventHandlers = {}
		this.eventHandlers.addedfile = this.fileAdded.bind(this);
		this.eventHandlers.init = (dropzone) => {
			this.dropzone = dropzone;
		}

		if(props.onMount != undefined) props.onMount(this);

		if(props.preview != undefined && !props.preview) djsConfig.previewsContainer = false;
		//If there is a Children override it
	}

	componentDidMount() {
		if (this.dropzone != undefined)
			this.dropzone.on('addedfile', (file) => {
				if(file) this.setState({ file_name:file.name });
			});
	}

	//Load the preview when loading the image
	fileAdded(file) {
		var reader = new FileReader();
		var allowed = (this.props.allowed != undefined) ? this.props.allowed : false

		reader.onload = (e) => {

			//Check for mime type
			var parts = e.target.result.split(';');
			var mime = parts[0].split(':');
			//mime[1] = 'application/octet-stream';
			let file_valid_extension = false;
			let recognized_mimetype = (mime[1] == 'application/octet-stream') ? false : true; //unknown mimetype exe && csv in windows (BINARY FILE)

			if(!recognized_mimetype){
				let filename = (this.state.file_name).toLowerCase()
				let exploded_filename = filename.split('.');
				let last_term = exploded_filename[exploded_filename.length - 1];

				let accepted_formats = this.props._accepted_formats != undefined ? this.props._accepted_formats : [];
				for(var format in accepted_formats) {
					if(last_term == accepted_formats[format].toLowerCase()){
						file_valid_extension=true;
						break;
					}
				}
			}

			let _error_field = null;

			if(allowed) {
				let not_in_alloweds= (typeof allowed === "object" && allowed[mime[1]] == undefined) || (mimetypes[allowed] != undefined && typeof allowed === "string" && mimetypes[allowed].indexOf(mime[1]) == -1);
				if(!recognized_mimetype && !file_valid_extension || not_in_alloweds && recognized_mimetype){
					_error_field = "Format not allowed";
					this.setState({
						file:null,
						image:null,
						size: getFileSize(e.total),
						error:_error_field
					});
					if(this.props.onError != undefined) this.props.onError(_error_field);
					if(this.props.change != undefined) this.props.change(this.props.name,null);
					return;
				}
			}

			const max_weight = this.props?._max_weight ?? null;//null BY default
			if(max_weight){
				const max_size_permitted = max_weight * 1024 * 1024;
				const size_file_uploaded = e.total;
				if(max_size_permitted<size_file_uploaded && !_error_field){
					_error_field = this.props.intl.formatMessage({id:"You have exceeded the maximum file size{max}"},{max:`: ${getFileSize(max_size_permitted)}`});
					this.setState({
						file:null,
						image:null,
						size: getFileSize(e.total),
						error:_error_field
					});
					if(this.props.onError != undefined) this.props.onError(_error_field);
					if(this.props.change != undefined) this.props.change(this.props.name,null);
					return;
				}
			}

			this.setState({
				image:e.target.result,
				file: e.target.result,
				size: getFileSize(e.total),
				error:_error_field
			});

			if(this.props.change != undefined) {
				const filename = (this.state.file_name != "") ? this.state.file_name : this.state.file;
				this.props.change(this.props.name,this.state.file,filename);
			}

			if(this.props.onError != undefined){
				this.props.onError(null);
			}
		}
		reader.readAsDataURL(file);
	}

	remove() {
		if(this.props.delete) this.props.delete();
		if(this.props.change != undefined) {
			this.props.change(this.props.name,null);
			this.props.change('full_' + this.props.name, null);
		}

		this.setState({
			file:null,
			file_name:null,
			image:null,
			error:null,
		});

		if(this.props.onRemove) this.props.onRemove();
	}

	has_preview() {
		if(this.props.preview_file == undefined || this.props.preview_file == "") return false;
		return (/^data\:/.test(this.props.preview_file)) ? false : true;
	}

	render() {
		let classname = this.props?.classname ?? 'input file';
		classname = parseClasses(classname, this.props._class, 'fileuploader');

		const dd_size = this.props?.dd_size ?? 'dynamic';
		const disabled = this.props?.disabled ?? false;

		if(disabled) classname += 'disabledField';

		const has_error = (this.props.errors && this.props.errors[this.props.name]) ? this.props.errors[this.props.name] : (this.state.error != null ? this.state.error : false);

		const _styles = this.props?._style ?? {};
		const _field_styles = this.props?._field_style ?? {};

		var dd_classes = 'image_uploader file_uploader with_dragger gui_pos_rel';
		if(this.state.file != '') dd_classes += ' no_img';
		if(dd_size == 'full_w') dd_classes += ' small_drag_message';
		let display_custom_dragging_instructions = (this.props.display_custom_dragging_instructions != undefined) ? this.props.display_custom_dragging_instructions : false;

		const max_weight = this.props?._max_weight ?? null;//null BY default
		let message_max_weight = false;
		if(max_weight){
			const max_size_permitted = max_weight * 1024 * 1024;
			message_max_weight = this.props.intl.formatMessage({id:"Maximum file size"})+': '+(getFileSize(max_size_permitted));
		}

		var accepted_formats = false;
		if(this.props._accepted_formats != undefined) {
			var accepted =[];
			for(var format in this.props._accepted_formats) {
				accepted.push(this.props.intl.formatMessage({id:this.props._accepted_formats[format]}));
			}
			accepted_formats = accepted.join(", ");
		}

		if(this.props.small_input != undefined && this.props.small_input) {
			classname += ' is_small_input';
			if(display_custom_dragging_instructions === false) display_custom_dragging_instructions = "Upload file";
		}

		const filename = (this.state.file_name != "") ? this.state.file_name : this.state.file;

		const display_dragging_instructions = (!display_custom_dragging_instructions) ? ((this.props.display_dragging_instructions != undefined) ? this.props.display_dragging_instructions : true) : false;

		const accepted_formats_position = (this.props.accepted_formats_position != undefined) ? this.props.accepted_formats_position : ((!window.is_rtl) ? 'mod_align_right' : 'mod_align_left');

		return (
			<div class={'react_field' + ((this.props._field_class != undefined) ? ' ' + this.props._field_class : '')} style={_field_styles}>
				<div class={classname+((has_error) ? ' has_error' : '')} style={_styles}>
					{this.props.children && (
						<FieldLabel
							{...this.props.label_props}
							name ={this.props.name}
							autotranslate={this.props.autotranslate}
						>{this.props.children}</FieldLabel>
					)}

					<div class="field_container this_input">
						<figure class={dd_classes}>
							<DropzoneComponent config={componentConfig} eventHandlers={this.eventHandlers} djsConfig={djsConfig}>
								{(filename != '' && filename != null) ? (
									<div class="uploader_preview img_preview_bg">
										<div class="dz-message has_file">
											<span>[ {this.props.intl.formatMessage({id:"File uploaded"})} ] {this.state.size && ("("+this.state.size+")")}</span>
										</div>
									</div>
								) : (
									<div>
										<div class="dz-message">
											{(display_dragging_instructions) && (
												<span class="drag_message">
													<span class="original_color_item">{this.props.intl.formatMessage({id:"Click to Select and Upload File"})}</span>
													<span class="original_color_item">{this.props.intl.formatMessage({id:"or"})}</span>
													<span class="original_color_item">{this.props.intl.formatMessage({id:"Drag and Drop File"})}</span>
												</span>
											)}

											{(display_custom_dragging_instructions) && (
												<span class="drag_message">
													<span class="original_color_item">{this.props.intl.formatMessage({id:display_custom_dragging_instructions})}</span>
												</span>
											)}
										</div>

										<img src={window.cdn_url + 'img/blank.png'} width="100%" height="80" />
									</div>
								)}
							</DropzoneComponent>

							{(this.state.file != '' && this.state.file != null) && (
								<ButtonCollection _class={{other:'floating_actions'}}>
									{(this.has_preview()) && (
										<SquaredButton
											_href={this.props.preview_file}
											_target="_blank"
											icon='knk-download transparentButton'
											aria-label="Download file"
										/>
									)}

									<DeleteButton
										squared={true}
										onClick={() => this.remove()}
									/>
								</ButtonCollection>
							)}

							{(this.state.error != '' && this.state.error != null) && (
								<ButtonCollection _class={{other:'floating_actions'}}>
									<span class="knk-off redIcon" />
								</ButtonCollection>
							)}
						</figure>

						{(this.props.after_component != undefined) && (
							<span class="label_additional_msg gui_after_message whole_contents">{this.props.after_component}</span>
						)}

						{(accepted_formats) && (
							<div class={'gui_after_message ' + accepted_formats_position}>
								{this.props.intl.formatMessage({id:"Valid formats"})}: {accepted_formats}

								{(message_max_weight) && (
									<span> | {message_max_weight}</span>
								)}

								{(isNotEmpty(this.props, 'after_message')) && (
									<span> | {this.props.after_message}</span>
								)}
							</div>
						)}

						{(!accepted_formats && isNotEmpty(this.props, 'after_message')) && (
							<div class="gui_after_message">{this.props.after_message}</div>
						)}

						{(has_error) && (
							<FieldErrorMessage error_msg_position_class={this.props.error_msg_position_class}>{has_error}</FieldErrorMessage>
						)}
					</div>
				</div>
			</div>
		);
	}
}

export default injectIntl(FileUploader);